import { motion } from 'motion/react';
import type { ReactNode } from 'react';

export type StampTone = 'ink' | 'red' | 'blue' | 'pencil' | 'orange';

export type StampProps = {
  children: ReactNode;
  /** Rotation degrees. Defaults to ~8° (slight tilt). */
  rotate?: number;
  /** Color tone — 'ink' is the legacy noir; the dossier set ships red/blue/pencil/orange. */
  tone?: StampTone;
  /** When true, plays the "thud" entrance animation (rotate-overshoot + scale). */
  animate?: boolean;
  className?: string;
};

const toneClass: Record<StampTone, string> = {
  ink: 'border-ink bg-cream text-ink shadow-[3px_3px_0_var(--color-ink)]',
  red: 'border-[var(--color-stamp-red)] text-[var(--color-stamp-red)] bg-[rgba(255,255,255,0.35)] shadow-[3px_3px_0_rgba(0,0,0,0.18)]',
  blue: 'border-[var(--color-blue-pen)] text-[var(--color-blue-pen)] bg-[rgba(255,255,255,0.35)] shadow-[3px_3px_0_rgba(0,0,0,0.18)]',
  pencil:
    'border-[var(--color-pencil)] text-[var(--color-pencil)] bg-[rgba(255,255,255,0.35)] shadow-[3px_3px_0_rgba(0,0,0,0.18)]',
  orange:
    'border-[var(--color-orange-mark)] text-[var(--color-orange-mark)] bg-[rgba(255,255,255,0.35)] shadow-[3px_3px_0_rgba(0,0,0,0.18)]',
};

/**
 * Inked rubber stamp — bordered slab with a hard offset shadow and uppercase
 * display text. Used for terminal banners ("CASE CLOSED", "EYES ONLY",
 * "ÉLIMINÉ", "CONFIDENTIEL") that punctuate phase transitions. The
 * `animate` flag plays a Spring-like overshoot ("thud") so the stamp
 * slams into place. `tone` switches the colour family — `'red'` is the
 * dossier verdict stamp.
 */
export const Stamp = ({
  children,
  rotate = 8,
  tone = 'ink',
  animate = false,
  className,
}: StampProps) => {
  const initial = animate ? { rotate: -22, scale: 1.7, opacity: 0 } : false;
  const target = animate
    ? { rotate, scale: [0.92, 1], opacity: [0.95, 0.85] }
    : { rotate, opacity: 0.85 };

  return (
    <motion.div
      initial={initial}
      animate={target}
      transition={{ duration: 0.55, ease: [0.18, 1.15, 0.4, 1] }}
      className={[
        'inline-block rounded-md border-[2.5px] px-3.5 py-1.5 font-display font-extrabold text-base uppercase tracking-[0.1em]',
        toneClass[tone],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </motion.div>
  );
};
